import { Children, isValidElement, useEffect, useId, type ReactNode } from "react";
import type { SxProps, Theme } from "@mui/material";
import { dismissNotice, publishNotice } from "./notification-bus";

const textContent = (node: ReactNode): string =>
  Children.toArray(node)
    .map((child): string =>
      isValidElement<{ children?: ReactNode }>(child)
        ? textContent(child.props.children)
        : typeof child === "string" || typeof child === "number"
          ? String(child)
          : "",
    )
    .join(" ");

/** Declarative errors share the same queue as action-triggered notifications. */
export const SnackbarNotice = ({
  children,
  severity = "info",
  action,
  onClose,
}: {
  children: ReactNode;
  severity?: "error" | "warning" | "info" | "success";
  action?: ReactNode;
  onClose?: () => void;
  sx?: SxProps<Theme>;
}) => {
  const id = useId();
  const message = textContent(children).trim();
  useEffect(() => {
    let active = true;
    // Parent notification subscription mounts after child effects.
    queueMicrotask(() => {
      if (active && message)
        publishNotice({
          id,
          message,
          content: children,
          action,
          tone: severity === "error" || severity === "warning" ? "error" : "default",
          onDismiss: onClose,
        });
    });
    return () => {
      active = false;
      dismissNotice(id);
    };
    // JSX nodes change identity on each render; only new messages enqueue notices.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- JSX identity changes must not requeue unchanged messages.
  }, [id, message, severity]);
  return null;
};
